import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import TrueImpactModal, {
  modalHeightAutoStyles,
  modalRadiusStyles,
} from 'shared/design-system/components/TrueImpactModal';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import TrueImpactTextfield from 'shared/design-system/components/TrueImpactTextfield';
import { useClient } from 'shared/apollo';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { getInvitationByToken } from './api';

const StyledDiv = styled.div`
  padding: ${SPACERS.large};
  display: flex;
  flex-direction: column;
  gap: ${SPACERS.medium};
  .claim-code-actions {
    display: flex;
    justify-content: flex-end;
    gap: ${SPACERS.small};
  }
`;

interface ClaimCodeDialogProps {
  open: boolean;
  onClose: () => void;
}

const ClaimCodeDialog: React.FC<ClaimCodeDialogProps> = ({ open, onClose }) => {
  const client = useClient();
  const { push } = useHistory();
  const [code, setCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(async () => {
    const token = code.trim();
    if (!client || !token) return;
    setIsValidating(true);
    setError(null);
    try {
      const { data } = await getInvitationByToken(client, token);
      const inv = data?.invitationByToken;
      if (inv?.investment?.fullyAllocated) {
        setError('This investment is already fully allocated.');
        return;
      }
      if (inv?.claimed && !inv.claimedByCurrentUser) {
        setError('This claim code has already been used.');
        return;
      }
      if (inv?.investment) {
        push(`/npo/allocate-investment?token=${encodeURIComponent(token)}`);
        return;
      }
      setError('This claim code is invalid or the investment is already allocated.');
    } catch (e) {
      setError('Unable to validate the claim code. Please try again.');
    } finally {
      setIsValidating(false);
    }
  }, [client, code, push]);

  return (
    <TrueImpactModal
      open={open}
      title="Claim an investment"
      maxWidth="480px"
      hideFooter={true}
      addStyles={`${modalHeightAutoStyles} ${modalRadiusStyles}`}
      onClose={onClose}
    >
      <StyledDiv>
        <TrueImpactText>
          Enter the claim code from your invitation email to allocate the investment.
        </TrueImpactText>
        <TrueImpactTextfield
          placeholder="Claim code"
          value={code}
          autoFocus={true}
          isError={!!error}
          onChange={value => setCode(value)}
          onEnterPress={handleSubmit}
        />
        {error && <TrueImpactText color="critical">{error}</TrueImpactText>}
        <div className="claim-code-actions">
          <TrueImpactButton variant="secondary-large" onClick={onClose}>
            Cancel
          </TrueImpactButton>
          <TrueImpactButton
            onClick={handleSubmit}
            disabled={!code.trim()}
            isLoading={isValidating}
          >
            Continue
          </TrueImpactButton>
        </div>
      </StyledDiv>
    </TrueImpactModal>
  );
};

export default ClaimCodeDialog;
